import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Lock, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "./Logo";

interface ComingSoonProps {
  onPasswordSubmit: (password: string) => void;
}

export const ComingSoon = ({ onPasswordSubmit }: ComingSoonProps) => {
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    setTimeout(() => {
      onPasswordSubmit(password);
      setIsLoading(false);
    }, 500);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingEmail(true);

    try {
      // Use email_preferences instead of email_signups for better segmentation
      const { error } = await supabase
        .from('email_preferences')
        .insert([{ 
          email,
          newsletter_consent: true,
          marketing_consent: false,
          user_type: 'guest', // Mark as guest signup (no account)
          subscribed_at: new Date().toISOString(),
        }]);

      if (error) {
        if (error.code === '23505') { // Unique violation
          toast({
            title: "Du är redan anmäld!",
            description: "Den här e-postadressen finns redan i vår lista.",
          });
        } else {
          throw error;
        }
      } else {
        setEmailSubmitted(true);
        toast({
          title: "Tack för din anmälan!",
          description: "Vi kommer att kontakta dig när vi lanserar.",
        });
        setEmail("");
      }
    } catch (error) {
      console.error('Error submitting email:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Vänligen försök igen senare.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-background via-background to-accent/5 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),transparent)]" />
      
      <Card className="w-full max-w-md relative animate-fade-in">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <Logo size="lg" />
          </div>
          <CardDescription className="text-lg">
            En ny plattform för nordisk konst
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-serif font-semibold">Kommer Snart</h2>
            <p className="text-muted-foreground">
              Vi förbereder något speciellt. Plattformen för att köpa och sälja unik konst, 
              ansluta med konstnärer och upptäcka kreativitet.
            </p>

            {/* Email signup form */}
            <form onSubmit={handleEmailSubmit} className="space-y-3 pt-2">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  Få uppdateringar om lansering
                </label>
                <div className="flex gap-2">
                  <Input
                    id="email"
                    type="email"
                    placeholder="din@email.se"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmittingEmail || emailSubmitted}
                    required
                    className="flex-1"
                  />
                  <Button 
                    type="submit" 
                    disabled={!email || isSubmittingEmail || emailSubmitted}
                    variant="secondary"
                  >
                    {isSubmittingEmail ? "..." : emailSubmitted ? "✓" : "Anmäl"}
                  </Button>
                </div>
              </div>
            </form>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Har du tillgång? Ange lösenord
              </label>
              <Input
                id="password"
                type="password"
                placeholder="Ange lösenord"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                className="text-center"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={!password || isLoading}
            >
              {isLoading ? "Kontrollerar..." : "Fortsätt"}
            </Button>
          </form>

          <div className="pt-4 border-t border-border">
            <p className="text-xs text-center text-muted-foreground">
              Intresserad av early access? Kontakta oss för mer information.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
